import type React from 'react';

import AchievementsPage from 'containers/Achievements/loadable';
import BizMiles from 'containers/BizMiles/loadable';
import Customers from 'containers/Customers/loadable';
import ErrorPage from 'containers/ErrorPage/loadable';
import Goals from 'containers/Goals/loadable';
import Investments from 'containers/Investments/loadable';
import Invoices from 'containers/Invoices/loadable';
import Acknowledge from 'containers/Legal/Acknowledgements/loadable';
import MileageTracker from 'containers/MileageTracker/loadable';
import NewReports from 'containers/NewReports/loadable';
import NotFoundPage from 'containers/NotFoundPage/loadable';
import OverviewPage from 'containers/OverviewPage/loadable';
import PickYourPlanPage from 'containers/PickYourPlanPage/loadable';
import PlanningToolsPage from 'containers/PlanningTools/loadable';
import Settings from 'containers/Settings/loadable';
import SetupPageAcme from 'containers/SetupPageAcme/loadable';
import SpendingPlanBase from 'containers/SpendingPlanBase/loadable';
import SpendingWatchlist from 'containers/SpendingWatchlist/loadable';
import UpcomingPage from 'containers/Upcoming/loadable';
import ViewInvoicePage from 'containers/ViewInvoice/loadable';
import { userIsAuthenticatedWithDataset } from 'utils/auth';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Automation from 'containers/Automation';
import SharedComponentsPage from 'containers/SharedComponentsPage/loadable';
/////////

import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';

import type { FeatureFlagsKeys } from 'app-components/flux/feature-flags/featureFlagsTypes';
import Login from './Login';
import Logout from './Logout';
import * as navIcons from './navIcons';
import useShareAccept from './useShareAccept';

const ProtectedDashboard = userIsAuthenticatedWithDataset(OverviewPage);
const ProtectedInvestments = userIsAuthenticatedWithDataset(Investments);
const ProtectedSettingsComponent = userIsAuthenticatedWithDataset(Settings);
const ProtectedNewReports = userIsAuthenticatedWithDataset(NewReports);
const ProtectedSpendingWatchlist = userIsAuthenticatedWithDataset(SpendingWatchlist);
const ProtectedSpendingPlanBase = userIsAuthenticatedWithDataset(SpendingPlanBase);
const ProtectedUpcomingPage = userIsAuthenticatedWithDataset(UpcomingPage);
const ProtectedGoals = userIsAuthenticatedWithDataset(Goals);
const ProtectedPlanningToolsPage = userIsAuthenticatedWithDataset(PlanningToolsPage);
const ProtectedMiles = userIsAuthenticatedWithDataset(BizMiles);
const ProtectedCustomers = userIsAuthenticatedWithDataset(Customers);
const ProtectedInvoices = userIsAuthenticatedWithDataset(Invoices);
const ProtectedMileageTracker = userIsAuthenticatedWithDataset(MileageTracker);

export const taxPlannerPath = '/tax-planner';
export const upcomingNavKey = 'upcoming';
export const mileageTrackerPath = '/mileage-tracker';
const dashboardNavKey = '';

type Group = 'Business' | 'Planning' | 'Insights';
export const navOrderV2 = [
  dashboardNavKey,
  'transactions',
  ['Business'],
  'clients',
  'invoices',
  mileageTrackerPath.replace(/^\//, ''),
  ['Planning'],
  'spending-plan',
  'goals',
  upcomingNavKey,
  'planning-tools',
  taxPlannerPath.replace(/^\//, ''),
  ['Insights'],
  'investing',
  'watchlist',
  'reports',
  'miles',
] as const satisfies (string | Group[])[];

export type FeaturePage = {
  /** title used in the QTip and used for the automation ID */
  title: string;
  /** used for path matching in Route */
  exact: boolean;
  /** Is the account list open or closed by default */
  open: boolean;
  /** if in navbar, which section is it in */
  nav: 'main' | 'sub';
  alt?: string;
  /** Navbar icon */
  icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  /** feature flag name if feature is to be shown/hidden based on flag */
  featureFlag?: FeatureFlagsKeys;
  path: string;
  component?: any;
  datasetNotNeeded: boolean;
  authNotNeeded: boolean;
  showSetupBar?: boolean;
  pathParams: string;
  hideNav: boolean;
  useSelectedAccount?: boolean;
  accountShowing: boolean;
  preserveDisplayNode?: boolean;
  noScroll?: boolean;
  addFIInitiator?: string;
  accountsShowing?: boolean;
  /** Hide page based on subscription tier */
  hideOnTiers: Readonly<string[]>;
  businessEnabled?: boolean;
};

const defaultPage = {
  title: '',
  exact: true,
  open: true,
  nav: 'main',
  alt: 'icon',
  iconOld: BlockOutlinedIcon,
  icon: BlockOutlinedIcon,
  datasetNotNeeded: false,
  authNotNeeded: false,
  accountShowing: false,
  hideNav: false,
  hideOnTiers: [],
  addFIInitiator: 'app',
  pathParams: '',
  businessEnabled: false,
} as const;

const featurePages: FeaturePage[] = [
  {
    ...defaultPage,
    alt: undefined,
    path: '/login',
    component: Login,
    exact: true,
    title: 'Login',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  {
    ...defaultPage,
    alt: undefined,
    path: '/automation',
    component: Automation,
    exact: true,
    title: 'Automation',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
///////////
  {
    ...defaultPage,
    path: '/logout',
    component: Logout,
    exact: true,
    title: 'Logout',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/',
    component: ProtectedDashboard,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Dashboard',
    alt: 'easel showing abstract chart',
    icon: navIcons.navIconDashboard,
    showSetupBar: true,
  },
  {
    ...defaultPage,
    path: '/investing',
    pathParams: '/:selectedInvestmentId?/:newsfeedArticleId?',
    component: ProtectedInvestments,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Investments',
    alt: 'bank vault with open door',
    icon: navIcons.navIconInvestments,
    featureFlag: 'investmentsAcme',
    noScroll: false,
  },
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  {
    ...defaultPage,
    path: '/docs',
    component: SharedComponentsPage,
    nav: 'main',
    exact: true,
    title: 'Docs',
    alt: 'Docs',
    icon: BuildOutlinedIcon,
    pathParams: '/:itemId?',
  },
///////////
  {
    ...defaultPage,
    path: '/settings',
    component: ProtectedSettingsComponent,
    nav: 'main',
    exact: true,
    title: 'Settings',
    alt: 'settings icon',
    icon: navIcons.navIconSettings,
    pathParams: '/:settingSelected?/:itemSelected?',
  },
  {
    ...defaultPage,
    path: '/reports',
    component: ProtectedNewReports,
    exact: true,
    nav: 'main',
    title: 'Reports',
    alt: 'various spending reports and charts',
    icon: navIcons.navIconReports,
    featureFlag: 'reports',
    // to enable different number of params, we have to use exact=true and repeat them
    pathParams: '/:tabName?/:groupBy?/:layoutView?',
  },
  {
    ...defaultPage,
    path: '/transactions',
    exact: true,
    open: true,
    nav: 'main',
    title: 'Transactions',
    alt: 'ledger of printed transactions',
    icon: navIcons.navIconTransactions,
    useSelectedAccount: true,
    preserveDisplayNode: false,
    noScroll: false,
    accountShowing: true,
    addFIInitiator: 'account list txns',
  },
  {
    ...defaultPage,
    path: '/watchlist',
    pathParams: '/:selectedWatchlistId?',
    component: ProtectedSpendingWatchlist,
    exact: true,
    nav: 'main',
    title: 'Watchlist',
    alt: 'bar graph with values',
    icon: navIcons.navIconWatchlist,
    featureFlag: 'spendingWatchList',
    noScroll: true,
  },
  {
    ...defaultPage,
    path: '/acknowledgements',
    component: Acknowledge,
    exact: true,
    title: 'Acknowledgements',
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/goals',
    component: ProtectedGoals,
    exact: true,
    nav: 'main',
    title: 'Savings Goals',
    alt: 'bar graph with values',
    icon: navIcons.navIconSavingsGoals,
    featureFlag: 'goals',
    pathParams: '/:goalId?',
  },
  {
    ...defaultPage,
    path: '/miles',
    component: ProtectedMiles,
    exact: true,
    nav: 'main',
    title: 'Miles',
    alt: 'bar graph with values',
    icon: navIcons.navIconBizMilesOld,
    featureFlag: 'bizMiles',
  },
  {
    ...defaultPage,
    path: '/spending-plan',
    pathParams: '/:tabs?/:selectedPlannedId?',
    component: ProtectedSpendingPlanBase,
    exact: true,
    nav: 'main',
    title: 'Spending Plan',
    alt: 'spending plan icon',
    icon: navIcons.navIconSpendingPlan,
    featureFlag: 'spendingPlan',
    noScroll: true,
  },
  {
    ...defaultPage,
    path: '/planning-tools',
    pathParams: '/:tabName?',
    component: ProtectedPlanningToolsPage,
    exact: true,
    nav: 'main',
    title: 'Planning Tools',
    alt: 'planning tools icon',
    icon: navIcons.navIconPlanningTools,
    featureFlag: 'reportsMoveRfndtrkrCrdtscr',
    noScroll: true,
  },
  {
    ...defaultPage,
    path: '/upcoming',
    component: ProtectedUpcomingPage,
    exact: true,
    nav: 'main',
    title: 'Bills & Income',
    alt: 'upcoming icon',
    icon: navIcons.navIconBillsAndPayments,
    featureFlag: 'upcoming',
    pathParams: '/:tabName?',
    noScroll: true,
  },
  {
    ...defaultPage,
    path: taxPlannerPath,
    title: 'Tax Planner',
    alt: 'Tax Planner',
    icon: navIcons.taxPlanner,
    featureFlag: 'taxPlanner',
  },
  {
    ...defaultPage,
    exact: true,
    path: '/get-started',
    component: SetupPageAcme,
    title: 'Getting Started',
    datasetNotNeeded: true,
    hideNav: true,
    accountsShowing: false,
  },
  {
    ...defaultPage,
    exact: true,
    component: AchievementsPage,
    path: '/achievements',
    title: 'Achievements',
    hideNav: false,
    accountShowing: false,
    featureFlag: 'achievements',
  },
  {
    ...defaultPage,
    exact: true,
    path: '/error',
    component: ErrorPage,
    title: 'Error',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    exact: true,
    path: '/oops',
    component: NotFoundPage,
    title: 'Oops',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/pick-your-plan',
    component: PickYourPlanPage,
    title: 'Pick Your Plan',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/view-invoice',
    title: 'View an Invoice',
    component: ViewInvoicePage,
    datasetNotNeeded: true,
    authNotNeeded: true,
    hideNav: true,
  },
  {
    ...defaultPage,
    path: '/share-accept',
    component: useShareAccept,
    exact: true,
    title: 'Share Accept',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/clients',
    component: ProtectedCustomers,
    exact: true,
    nav: 'main',
    title: 'Clients & Projects',
    alt: 'clients icon',
    icon: navIcons.navIconClients,
    featureFlag: 'businessEnabled',
    businessEnabled: true,
    noScroll: true,
    pathParams: '/:customerId?',
  },
  {
    ...defaultPage,
    path: '/invoices',
    component: ProtectedInvoices,
    exact: true,
    nav: 'main',
    title: 'Invoices',
    alt: 'invoices icon',
    icon: navIcons.navIconInvoice,
    featureFlag: 'businessEnabled',
    businessEnabled: true,
    noScroll: true,
  },
  {
    ...defaultPage,
    path: mileageTrackerPath,
    component: ProtectedMileageTracker,
    exact: true,
    nav: 'main',
    title: 'Mileage',
    alt: 'mileage icon',
    icon: navIcons.navIconMileageIcon,
    featureFlag: 'qbpMileageTracker',
    noScroll: true,
  },
  // UP UP UP ^^^ - insert new components above that line - NotFoundPage have to be the last one -----------------------
  {
    ...defaultPage,
    exact: true,
    path: '',
    component: NotFoundPage,
    title: 'Not Found Page',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  // NO! NO! NO! - please don't add any components here - NotFoundPage have to be the last one -------------------------
] as const;

export const getNavFromKey = (key: string) => featurePages.find((x) => x.path === `/${key}`);

export const makeNavigationItemId = (nav: FeaturePage) => {
  return `nav-menu-${nav.title || 'default'}`
    .toLowerCase()
    .replaceAll?.(' ', '-')
    .replace('/', '')
    .replace('&', '')
    .replaceAll?.('--', '-');
};

export const findFeaturePageByPath = (path: string) => {
  const keys = path.split('/');
  const featurePagesMatches = featurePages
    .map((featurePage) => {
      const levels = featurePage.path.split('/');
      const levelsMatch = levels.reduce(
        (matched, currentLevel, index) => (keys[index] === currentLevel ? matched + 1 : matched),
        0,
      );
      return { levelsMatch, levels, featurePage };
    })
    .sort((item1, item2) => item2.levelsMatch - item1.levelsMatch);
  const featurePageBestMatch = featurePagesMatches[0];
  return featurePageBestMatch.levelsMatch === featurePageBestMatch.levels.length
    ? featurePageBestMatch.featurePage
    : undefined;
};
